import React from "react";
import { CalendarDateInput, MonthDayCalendarValue } from "../common";
import { useCmpOrganizationNoun } from "../../../../../contexts/CmpOrganizationNounContext";
import { formatStaffPhoneInput } from "../../../../../utils/memberContactFormatting";

// Claude Design Global Settings blocks backed by settings fields
// `setup.multipleBillingDates` (D-CS1) and
// `paymentsAndTaxes.invoiceBillingContact` (D-CS2).
export type MultipleBillingDatesValue = {
  enabled: boolean;
  firstBillingDate: MonthDayCalendarValue;
  secondBillingDate: MonthDayCalendarValue;
};

export type InvoiceBillingContactValue = {
  name: string;
  email: string;
  phone: string;
};

// One helper for both dates: new members renew on the next billing date
// after they join (the proration rule in settingsProration.ts).
const MULTIPLE_BILLING_DATE_HELPER =
  "New members renew on whichever billing date comes next after they join.";

export const globalSettingsDesignInputClassName =
  "h-10 w-full rounded-lg border border-[#9E9E9E] bg-white px-3 py-[10px] text-sm leading-[18px] text-[#0A0A0A] outline-none disabled:cursor-not-allowed";

type MultipleBillingDatesFieldProps = {
  value: MultipleBillingDatesValue;
  onChange: (value: MultipleBillingDatesValue) => void;
  // The tab's ENABLED/DISABLED switch, so every Global Settings toggle matches.
  toggle: React.ReactNode;
};

// Design D:5385-5438. The dates depend only on this block's own toggle, not
// on Prorated Company Membership Fees.
export const MultipleBillingDatesField = ({
  value,
  onChange,
  toggle,
}: MultipleBillingDatesFieldProps) => {
  const noun = useCmpOrganizationNoun();
  const dateInputs = [
    {
      key: "firstBillingDate",
      title: "First Billing Date",
      helper:
        MULTIPLE_BILLING_DATE_HELPER,
      ariaLabel: "First billing date",
    },
    {
      key: "secondBillingDate",
      title: "Second Billing Date",
      helper:
        MULTIPLE_BILLING_DATE_HELPER,
      ariaLabel: "Second billing date",
    },
  ] as const;

  return (
    <div className="mt-10 flex flex-col gap-[10px]">
      <div>
        <h3 className="text-base font-semibold text-[#0A0A0A]">
          Multiple Membership Billing Dates
        </h3>
        <p className="mt-1 text-sm text-[#0A0A0A]">
          If your {noun.singular} bills memberships on more than one date each Fiscal
          Year, please enable and set each billing date.
        </p>
      </div>
      {toggle}
      <div className="flex flex-wrap gap-3">
        {dateInputs.map((dateInput) => (
          <div key={dateInput.key} className="min-w-0 flex-[1_1_150px]">
            <div className="text-[13px] font-semibold text-[#0A0A0A]">
              {dateInput.title}
            </div>
            <div className="mb-[6px] mt-[2px] text-[12.5px] text-[#0A0A0A]">
              {dateInput.helper}
            </div>
            <CalendarDateInput
              mode="monthDay"
              ariaLabel={dateInput.ariaLabel}
              disabled={!value.enabled}
              value={value[dateInput.key]}
              onChange={(date) => onChange({ ...value, [dateInput.key]: date })}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

type InvoiceBillingContactFieldsProps = {
  value: InvoiceBillingContactValue;
  onChange: (value: InvoiceBillingContactValue) => void;
};

// Design D:5827-5840 (Invoicing Options).
export const InvoiceBillingContactFields = ({
  value,
  onChange,
}: InvoiceBillingContactFieldsProps) => {
  const fields = [
    {
      key: "name",
      label: "Contact Name",
      placeholder: "Enter Contact Name",
      type: "text",
    },
    {
      key: "email",
      label: "Contact Email",
      placeholder: "accounts@yourchamber.ca",
      type: "email",
    },
    {
      key: "phone",
      label: "Contact Phone Number",
      placeholder: "(807) 555-0140",
      type: "tel",
    },
  ] as const;

  return (
    <div className="flex flex-col gap-[10px]">
      <h4 className="text-base font-semibold text-[#0A0A0A]">
        Invoice Billing Contact Details
      </h4>
      <p className="text-sm text-[#0A0A0A]">
        This information will appear in the bottom section of every invoice and
        receipt created.
      </p>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {fields.map((field) => (
          <label key={field.key} className="flex min-w-0 flex-col gap-[6px]">
            <span className="text-sm font-semibold text-[#0A0A0A]">
              {field.label}
            </span>
            <input
              type={field.type}
              value={value[field.key]}
              onChange={(event) =>
                onChange({ ...value, [field.key]: field.key === "phone" ? formatStaffPhoneInput(event.target.value) : event.target.value })
              }
              placeholder={field.placeholder}
              className={globalSettingsDesignInputClassName}
            />
          </label>
        ))}
      </div>
    </div>
  );
};
